import { useMemo } from 'react';
import type { Room, WorldType, GameObject as GameObjectType } from '@hollowveil/shared';
import { isVisibleInWorld, useGameStore } from '@hollowveil/shared';
import { GameObject } from './GameObject';
import { PettableNPC } from './PettableNPC';

interface RoomRendererProps {
  room: Room;
  world: WorldType;
}

export function RoomRenderer({ room, world }: RoomRendererProps) {
  const solvedRiddles = useGameStore((state) => state.solvedRiddles);

  // Filter objects visible in current world, considering riddle-based visibility
  const visibleObjects = useMemo(() => {
    return room.objects.filter((obj) => {
      // First check world visibility
      if (!isVisibleInWorld(obj, world)) return false;

      // Check showAfterRiddleSolved - object should only appear if riddle is solved
      const showAfterRiddle = obj.properties?.showAfterRiddleSolved as string | undefined;
      if (showAfterRiddle && !solvedRiddles.has(showAfterRiddle)) {
        return false;
      }

      // Check hideAfterRiddleSolved - object should disappear after riddle is solved
      const hideAfterRiddle = obj.properties?.hideAfterRiddleSolved as string | undefined;
      if (hideAfterRiddle && solvedRiddles.has(hideAfterRiddle)) {
        return false;
      }

      return true;
    });
  }, [room.objects, world, solvedRiddles]);

  return (
    <group>
      {/* Render all visible objects */}
      {visibleObjects.map((obj) => (
        <GameObject key={obj.id} object={obj} world={world} />
      ))}

      {/* Render pettable NPCs/animals */}
      {room.npcs?.map((npc) => (
        <PettableNPC key={npc.id} npc={npc} world={world} />
      ))}

      {/* Room boundaries - subtle walls */}
      <RoomWalls size={room.size} world={world} />
    </group>
  );
}

interface RoomWallsProps {
  size: [number, number, number];
  world: WorldType;
}

function RoomWalls({ size, world }: RoomWallsProps) {
  const [width, height, depth] = size;
  const wallThickness = 0.2;
  const wallHeight = 3;

  const wallColor = world === 'real' ? '#D9C4B8' : '#566573';
  const wallOpacity = 0.3;

  return (
    <group>
      {/* Back wall */}
      <mesh position={[width / 2, wallHeight / 2, -wallThickness / 2]}>
        <boxGeometry args={[width + wallThickness * 2, wallHeight, wallThickness]} />
        <meshStandardMaterial
          color={wallColor}
          transparent
          opacity={wallOpacity}
          roughness={0.8}
        />
      </mesh>

      {/* Left wall */}
      <mesh position={[-wallThickness / 2, wallHeight / 2, depth / 2]}>
        <boxGeometry args={[wallThickness, wallHeight, depth + wallThickness * 2]} />
        <meshStandardMaterial
          color={wallColor}
          transparent
          opacity={wallOpacity}
          roughness={0.8}
        />
      </mesh>
    </group>
  );
}
